import {Injectable} from '@angular/core';
import {RequesterService} from "../../../shared/services/requester/requester.service";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {BackupMapper} from "./backup-mapper";
import {JobMapper} from "./job-mapper";
import {BehaviorSubject, Observable} from "rxjs";
import {BackupRestoreProgressMapper} from "./backup-restore-progress-mapper";
import {JobRunProgressMapper} from "./job-run-progress-mapper";
import {Store} from '@ngrx/store';
import * as fromStore from '../../../store';
import {DbStoreState} from '../../../store';
import {Config} from "../../../config/config";
import {BackupLimitationsMapper} from "./backup-limitations-mapper";
import {Subscription} from "rxjs";
import {filter, first, map} from "rxjs/operators";
import mockData from "../../../../assets/json/mock-data.json";

export interface IBackupDownloadState {

  checkpointId: string;
  completed: boolean;
  success: boolean;
  progressPercent: number;
  blob: Blob;
  message: string;

}

@Injectable()
export class BackupService {
  public backupActivity: Subscription;
  private currentWorkspaceId: any;
  private ownerWorkspaceId: any;

  constructor(
    private _requester: RequesterService,
    private _store: Store<DbStoreState>
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe(workspaceIdEntities => {
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public getDefaultBackups(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['settings-backup'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/jobs/default/backups`, null, true)
        .pipe(
          map((response: DbResponse): DbResponse => {
            if (response && response.success) {
              response.data = response.data.map(BackupMapper.map);
            }

            return response;
          })
        )
    );
  }

  public getBackupsList(websiteId: number) {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['settings-backup'];
      return new BehaviorSubject<DbResponse>(response);
    }
    const cacheBustingParams = Date.now().toString();
    return this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backup/list`, {cacheBustingParams});
  }


  public restoreBackup(websiteId: number, backupId: number | string, checkpointId: number | string, restoreSettings: any): BehaviorSubject<DbResponse> {
    // TODO: all agreement get to post
    const restoreTypes: string[] = [];
    if (restoreSettings.db) {
      restoreTypes.push('db');
    }
    if (restoreSettings.files) {
      restoreTypes.push('files');
    }

    const restoreData: any = {
      restore_settings: JSON.stringify(restoreTypes)
    };

    return this._requester.backup
      .post(`workspaces/${this.currentWorkspaceId}/backups/${backupId}/checkpoints/${checkpointId}/restore`, null, restoreData);
  }

  public restoreFromDomain(websiteId: number, sourceDomainId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backup/restore-from-domain`, null, {source_domain_id: sourceDomainId});
  }

  public restoreNewBackup(domainId: number, backupId: number): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${domainId}/backup/${backupId}/restore`, null);
  }

  public downloadNewBackupZip(domainId: number, backupId: number): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${domainId}/backup/${backupId}/download`, null);
  }

  public updateNewBackupSettings(domainId: number, hour: string | null, weekDay: string, dayOfMonth: string, hourRemainder: string | null, startTime: string | null): Observable<DbResponse> {
    const data = {
      hour: hour,
      weekday: weekDay,
      day_of_month: dayOfMonth,
      hour_remainder: hourRemainder,
      backup_start_time: startTime
    };

    return <Observable<DbResponse>>this._requester.lxd
      .put(`workspaces/${this.currentWorkspaceId}/domains/${domainId}/backup/settings`, null, data);
  }

  public runBackup(domainId): Observable<DbResponse> {
    return <Observable<DbResponse>>this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${domainId}/backup/run`, null, null);
  }


  public checkBackupRestoreProgress(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/restore-state/active`, null, true)
        .pipe(
          map((response: DbResponse): DbResponse => {
            if (response && response.success) {
              response.data = BackupRestoreProgressMapper.map(response.data);
            }

            return response;
          })
        )
    );
  }

  public editDefaultJobs(websiteId: number, data: any): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/jobs/default/edit`, null, data)
    );
  }

  public getDefaultJobs(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['default-backup'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/jobs/default`, null, true)
        .pipe(
          map((response: DbResponse): DbResponse => {
            if (response && response.success) {
              response.data = JobMapper.map(response.data);
            }

            return response;
          })
        )
    );
  }

  public getBackupLimitations(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['settings-backup'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, true)
        .pipe(
          map((response: DbResponse): DbResponse => {
            if (response && response.success) {
              response.data = BackupLimitationsMapper.map(response.data);
            }

            return response;
          })
        )
    );
  }


  public updateBackupLimitations(websiteId: number, deleteCount: number): BehaviorSubject<DbResponse> {
    const data = {
      "delete_count": deleteCount
    };
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .patch(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, data)
        .pipe(
          map((response: DbResponse): DbResponse => {
            return response;
          })
        )
    );
  }

  public runJob(websiteId: number, jobId: number | string): BehaviorSubject<DbResponse> {
    return this._requester.backup
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/jobs/${jobId}/run`);
  }

  public deleteAllBackups(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.backup
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backups/delete-all`);
  }

  public checkRunJobProgress(websiteId: number, jobId: number | string): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.backup
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/run-state/active`, null, true)
        .pipe(
          map((response: DbResponse): DbResponse => {
            if (response && response.success) {
              response.data = JobRunProgressMapper.map(response.data);
            }

            return response;
          })
        )
    );
  }

  public downloadBackupZip(websiteId: number, backupId: string, checkpointId: string): void {
    this._requester.backup
      .post(`workspaces/${this.currentWorkspaceId}/backups/${backupId}/checkpoints/${checkpointId}/process-download`);
  }

  public downloadBackupCheckpoint(websiteId: number, backupId: string, checkpointId: string): BehaviorSubject<IBackupDownloadState> {
    const downloadState$: BehaviorSubject<IBackupDownloadState> = new BehaviorSubject<IBackupDownloadState>(<IBackupDownloadState>{
      checkpointId: checkpointId,
      completed: false,
      success: false,
      progressPercent: 1,
      blob: null,
      message: 'Creating Archive...'
    });

    this._requester.backup
      .post(`workspaces/${this.currentWorkspaceId}/backups/${backupId}/checkpoints/${checkpointId}/process-download`)
      .pipe(
        first(response => !!response)
      )
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this.startCheckingBackupDownloadInitializationState(websiteId, backupId, checkpointId, downloadState$);
        } else {
          downloadState$.next(<IBackupDownloadState>{
            checkpointId: checkpointId,
            completed: true,
            success: false,
            progressPercent: 100,
            blob: null,
            message: response.message
          });
        }
      });

    return downloadState$;
  }


  private startCheckingBackupDownloadInitializationState(websiteId: number, backupId: string, checkpointId: string, downloadState$: BehaviorSubject<IBackupDownloadState>): void {
    this.backupActivity = this._store.select(fromStore.getBackupOngoingActivity(websiteId, Config.backupPluginSlug))
      .subscribe((data) => {
        if (data && data.action === 'process_download') {
          switch (data.status) {
            case 'running':
              downloadState$.next(<IBackupDownloadState>{
                checkpointId: checkpointId,
                completed: false,
                success: false,
                progressPercent: 1 + data.progress * 0.49,
                blob: null,
                message: data.message
              });
              break;

            case 'done':

              this.startDownloadingBackup(websiteId, backupId, checkpointId, downloadState$);
              this.backupActivity.unsubscribe();
              break;
          }
        }
      });
  }


  private startDownloadingBackup(websiteId: number, backupId: string, checkpointId: string, downloadState$: BehaviorSubject<IBackupDownloadState>): void {
    let progressPercent: number = 50;

    downloadState$.next(<IBackupDownloadState>{
      checkpointId: checkpointId,
      completed: false,
      success: false,
      progressPercent: progressPercent,
      blob: null,
      message: 'Downloading...'
    });

    let progressInterval: any = setInterval(() => {
      progressPercent += 2;

      downloadState$.next(<IBackupDownloadState>{
        checkpointId: checkpointId,
        completed: false,
        success: false,
        progressPercent: progressPercent,
        blob: null,
        message: 'Downloading...'
      });

      if (progressPercent >= 98) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    }, 4000);

    this._requester.backup
      .get(`workspaces/${this.currentWorkspaceId}/backups/${backupId}/checkpoints/${checkpointId}/download`, null, null, true)
      .pipe(
        filter((response: DbResponse) => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        if (progressInterval) {
          clearInterval(progressInterval);
          progressInterval = null;
        }

        if (response.success) {
          downloadState$.next(<IBackupDownloadState>{
            checkpointId: checkpointId,
            completed: true,
            success: true,
            progressPercent: 100,
            blob: response.data,
            message: 'Done.'
          });
        } else {
          downloadState$.next(<IBackupDownloadState>{
            checkpointId: checkpointId,
            completed: true,
            success: false,
            progressPercent: 100,
            blob: null,
            message: response.message
          });
        }
      });
  }
}
